/**
 * i18next Dynamic Localization Configuration
 * 
 * This module implements a dynamic localization system that switches between
 * Experilabs and Insider translation files based on user authentication and
 * selected factory.
 * 
 * Behavior:
 * - When user is NOT logged in: Uses Experilabs localization files
 * - When user IS logged in:
 *   - If selectedFactory.name === "insider 2": Uses Insider localization files
 *   - Otherwise: Uses Experilabs localization files
 * 
 * The localization automatically updates when:
 * - User logs in
 * - User logs out (via removeSelectedFactory in AppContext)
 * - selectedFactory changes (e.g., user switches to a different factory)
 * 
 * Integration:
 * - AppContext monitors selectedFactory changes and triggers updateLocalizationFromStorage()
 * - All components using useTranslation() will automatically receive updated translations
 */

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import experilabsEng from "./locales/experilabs/en.json";
import experilabsTr from "./locales/experilabs/tr.json";
import insiderEng from "./locales/insider/en.json";
import insiderTr from "./locales/insider/tr.json";


const langDetectorOptions = {
  order: ["localStorage", "cookie", "htmlTag", "navigator"],
  lookupCookie: "locale",
  lookupLocalStorage: "locale",
  caches: ["localStorage", "cookie"],
};

// Localization resources for different tenants
const localizationResources = {
  experilabs: {
    en: experilabsEng,
    tr: experilabsTr,
  },
  insider: {
    en: insiderEng,
    tr: insiderTr,
  },
};

/**
 * Determines which tenant's localization to use based on selectedFactory
 * @returns {string} - 'experilabs' or 'insider'
 */
export const getCurrentTenant = () => {
  try {
    const selectedFactory = localStorage.getItem("selectedFactory");
    if (!selectedFactory) {
      // User is not logged in, use experilabs
      return "experilabs";
    }

    const factory = JSON.parse(selectedFactory);
    // Check if the factory name is "insider 2"
    if (factory?.name?.toLowerCase() === "insider 2") {
      return "insider";
    }

    // Default to experilabs for all other cases
    return "experilabs";
  } catch (error) {
    console.error("Error determining tenant:", error);
    return "experilabs";
  }
};

/**
 * Loads the appropriate localization resources based on the current tenant
 * @param {string} tenant - 'experilabs' or 'insider'
 */
const loadTenantResources = (tenant) => {
  const resources = localizationResources[tenant] || localizationResources.experilabs;
  
  return {
    en: { translation: resources.en },
    tr: { translation: resources.tr },
  };
};

/**
 * Switches the localization to the specified tenant
 * @param {string} tenant - 'experilabs' or 'insider'
 */
export const switchLocalization = async (tenant) => {
  const resources = loadTenantResources(tenant);
  const currentLanguage = i18n.language || 'en';

  // Store current tenant for comparison
  if (i18n.currentTenant === tenant) {
    // Already using this tenant, no need to switch
    console.log(`Already using ${tenant} localization, skipping switch`);
    return;
  }

  console.log(`[i18n] Switching localization from ${i18n.currentTenant || 'initial'} to ${tenant}`);
  console.log(`[i18n] Current language: ${currentLanguage}`);

  try {
    // Strategy: Directly manipulate i18n's internal resource store
    // This is the most reliable way to ensure React components get new translations
    
    // 1. Get direct access to the resource store
    const resourceStore = i18n.services.resourceStore;
    console.log(`[i18n] Step 1: Accessing resource store`);
    
    // 2. Clear existing data
    if (resourceStore && resourceStore.data) {
      console.log(`[i18n] Step 2: Clearing existing resources`);
      Object.keys(resourceStore.data).forEach(lang => {
        if (resourceStore.data[lang] && resourceStore.data[lang].translation) {
          delete resourceStore.data[lang].translation;
        }
      });
    }
    
    // 3. Add new resources directly to the store
    console.log(`[i18n] Step 3: Adding new resources for tenant: ${tenant}`);
    Object.keys(resources).forEach((lang) => {
      if (!resourceStore.data[lang]) {
        resourceStore.data[lang] = {};
      }
      resourceStore.data[lang].translation = resources[lang].translation;
      console.log(`[i18n] Added ${lang} resources, keys count:`, Object.keys(resources[lang].translation).length);
    });
    
    // Mark current tenant
    i18n.currentTenant = tenant;
    
    // 4. Force i18n to notify all listeners about the change
    console.log(`[i18n] Step 4: Forcing language reload`);
    
    // Change to opposite language
    const oppositeLang = currentLanguage === 'en' ? 'tr' : 'en';
    await i18n.changeLanguage(oppositeLang);
    
    // Wait a bit for React to process
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Change back to original language
    await i18n.changeLanguage(currentLanguage);
    
    // 5. Emit all necessary events
    console.log(`[i18n] Step 5: Emitting events`);
    i18n.emit('languageChanged', currentLanguage);
    i18n.emit('loaded', resourceStore.data);
    
    console.log(`[i18n] ✅ Localization switched to ${tenant} successfully`);
    
    // Return true to indicate success
    return true;
  } catch (error) {
    console.error(`[i18n] ❌ Error switching localization:`, error);
    return false;
  }
};


/**
 * Updates localization based on the current selectedFactory in localStorage
 * This should be called when selectedFactory changes
 */
export const updateLocalizationFromStorage = async () => {
  const tenant = getCurrentTenant();
  console.log(`updateLocalizationFromStorage called, detected tenant: ${tenant}`);
  await switchLocalization(tenant);
};

// Initialize i18n with the appropriate tenant resources
const initialTenant = getCurrentTenant();
const initialResources = loadTenantResources(initialTenant);

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources: initialResources,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    detection: langDetectorOptions,
  });

// Set initial tenant marker
i18n.currentTenant = initialTenant;

// Store current tenant globally so AppContext can detect changes
window.i18nCurrentTenant = initialTenant;
console.log(`[i18n] Initial tenant set to: ${initialTenant}`);

export default i18n;

